import React from 'react';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import { PredictedPaiParams, PublishedParams, PredictedIktosParams, ProjectSynthesisPartner } from '@Pages/RetroSynthesis/constants';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { getPredictedProcessingTimeLabelByValue } from '@Pages/RetroSynthesis/utils';
import { isRPSChecked } from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/mapRCSToRPS';
import {
  isIktosPredictiveRetrosynthesisEnabled,
  isPaiPredictiveRetrosynthesisEnabled,
  updatePredictedParamsFromBE,
} from '../../predictiveUtils';
import { RCS10D, RCSANY, RPS_LIBRARIES } from '../../ProjectParametersDialog/constants';
import IntermediatesParameter from './IntermediatesParameter';

import './ProjectParametersGridItem.scss';

// eslint-disable-next-line consistent-return
export const paramsToString = (paramName, value, isPredicted, params) => {
  if (isPredicted) {
    if (params.partner === ProjectSynthesisPartner.PAI) {
      switch (paramName) {
        case PredictedPaiParams.FULL_SYNTHESIS_PLAN:
          return value ? `<b>${params[PredictedPaiParams.NUMBER_OF_PLANS]}</b> full routes (up to)` : 'Last step only';
        case PredictedPaiParams.IDENTICAL_REACTIONS_STEPS:
          return `<b>${value}</b> identical reaction steps per project (up to)`;
        case PredictedPaiParams.IDENTICAL_BUILDING_BLOCKS:
          return `<b>${value}</b> identical building blocks per project (up to)`;
        case PredictedPaiParams.PROCESSING_TIME:
          return `<b>${getPredictedProcessingTimeLabelByValue(value)}</b> processing time`;
        case PredictedPaiParams.USED_BUILDING_BLOCKS:
          return `<b>${value && value.join(' ')}</b> building blocks`;
        case PredictedPaiParams.INCLUDE_RPS:
          return `Published routes integration: ${value ? '<b>Supported</b>' : '<b>Ignored</b>'}`;
        default:
          return undefined;
      }
    }
    if (params.partner === ProjectSynthesisPartner.IKTOS) {
      switch (paramName) {
        case PredictedIktosParams.MAX_NO_OF_STEPS:
          return value === 1 ? 'First disconnect only' : `<b>${value}</b> steps per route (up to)`;
        case PredictedIktosParams.SELECTIVITY:
          return value ? 'Regioselective <b>reactions only</b>' : 'Regioselectivity <b>ignored</b>';
        case PredictedIktosParams.STEREO:
          return value ? 'Stereochemistry <b>supported</b>' : 'Stereochemistry <b>ignored</b>';
        case PredictedIktosParams.SHIPPING_TIME:
          // eslint-disable-next-line no-nested-ternary
          return !params.isRCSUnchecked
            ? value.indexOf(RCS10D) >= 0
              ? 'RCS: delivery time <b>up to 10 days</b>'
              : 'RCS: <b>any</b> delivery time'
            : null;
        case PredictedIktosParams.MAX_PRICE_PER_GRAM:
          // eslint-disable-next-line no-nested-ternary
          return !params.isRCSUnchecked ? (value ? `RCS: <b>${value} ($/g)` : `RCS: <b>no</b> price limit`) : null;
        case PredictedIktosParams.USED_BUILDING_BLOCKS:
          return `<b>${value && value.join(' ')}</b> building blocks`;
        case PredictedPaiParams.INCLUDE_RPS:
          return `Published routes integration: ${value ? '<b>Supported</b>' : '<b>Ignored</b>'}`;
        case PredictedIktosParams.PROCESSING_TIME:
          return `<b>${getPredictedProcessingTimeLabelByValue(value)}</b> processing time`;
        case PredictedIktosParams.INCLUDE_SUBSTRUCTURES:
          return value ? `<b>${value}</b> intermediates included` : '<b>No</b> intermediates defined';
        case PredictedIktosParams.EXCLUDE_SUBSTRUCTURES:
          return `<b>${value}</b> intermediates excluded`;
        default:
          return undefined;
      }
    }
  } else {
    switch (paramName) {
      case PublishedParams.FULL_SYNTHESIS_PLAN:
        return value ? `<b>${params[PublishedParams.NUMBER_OF_PLANS]}</b> full routes (up to)` : 'Last step only';
      case PublishedParams.MAX_BRANCH:
        return `<b>${value}</b> branches per step (up to)`;
      case PublishedParams.MAX_DEPTH:
        return `<b>${value}</b> steps per route (up to)`;
      case PublishedParams.STOP_COMMAND:
        return `<b>${value ? 'Stop' : "Don't Stop"}</b> at commercial building blocks`;
      case PublishedParams.DEFAULT_YIELD:
        return `<b>${value}%</b> yield per step (assumed, if not published)`;
      default:
        return undefined;
    }
  }
};

export function ParamItem({ paramName, value, paramType, params, isPredicted, step }) {
  let text = paramsToString(paramName, value, isPredicted, params);
  let isHighlighted = false;
  if (isPredicted && step) {
    isHighlighted = params.highlightedParams && params.highlightedParams.includes(paramName);
    if (isHighlighted) text = `<b>*${text}</b>`;
  }

  return !isNil(value) ? <InnerHTML className={`param ${paramType}-param param--${paramName}`} tag="div" html={text} /> : null;
}

ParamItem.propTypes = {
  paramName: PropTypes.string,
  value: PropTypes.any,
  paramType: PropTypes.string,
  params: PropTypes.object,
  isPredicted: PropTypes.bool,
  step: PropTypes.number,
};

export function ProjectParametersGridItem({ publishedParameters = {}, predictedParameters = {}, step }) {
  const predictiveIktosEnabled = isIktosPredictiveRetrosynthesisEnabled();
  const predictivePAIEnabled = !predictiveIktosEnabled && isPaiPredictiveRetrosynthesisEnabled();
  let predictedParametersUpdated = { ...predictedParameters };
  if (predictiveIktosEnabled && Object.keys(predictedParameters).length) {
    const { usedBuildingBlocks } = predictedParametersUpdated;
    let filteredBuildingBLocks = usedBuildingBlocks && usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) < 0);
    // Exclude RPS from usedBuildingBlocks, it will be handled by separate param PredictedIktosParams.INCLUDE_RPS
    filteredBuildingBLocks = filteredBuildingBLocks && filteredBuildingBLocks.filter((block) => !RPS_LIBRARIES.includes(block));
    const anyIntermediates = predictedParametersUpdated.includeSubstructures || predictedParametersUpdated.excludeSubstructures;
    predictedParametersUpdated = {
      ...updatePredictedParamsFromBE(predictedParametersUpdated),
      includeSubstructures:
        // eslint-disable-next-line no-nested-ternary
        predictedParametersUpdated.includeSubstructures
          ? predictedParametersUpdated.includeSubstructures.map((s) => ({ value: s }))
          : anyIntermediates
            ? []
            : null,
      // eslint-disable-next-line no-nested-ternary
      excludeSubstructures: predictedParametersUpdated.excludeSubstructures
        ? predictedParametersUpdated.excludeSubstructures.map((s) => ({ value: s }))
        : anyIntermediates
          ? []
          : null,
      shippingTime: usedBuildingBlocks ? usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) >= 0) : null,
      usedBuildingBlocks: filteredBuildingBLocks && filteredBuildingBLocks.length ? filteredBuildingBLocks : null,
      [PredictedIktosParams.INCLUDE_RPS]: isRPSChecked(usedBuildingBlocks),
    };
  } else if (predictivePAIEnabled) {
    const { usedBuildingBlocks } = predictedParametersUpdated;
    // Exclude RPS from usedBuildingBlocks, it will be handled by separate param PredictedPaiParams.INCLUDE_RPS
    const filteredBuildingBLocks = usedBuildingBlocks && usedBuildingBlocks.filter((block) => !RPS_LIBRARIES.includes(block));
    predictedParametersUpdated = {
      ...predictedParametersUpdated,
      usedBuildingBlocks: filteredBuildingBLocks && filteredBuildingBLocks.length ? filteredBuildingBLocks : null,
      [PredictedPaiParams.INCLUDE_RPS]: isRPSChecked(usedBuildingBlocks),
    };
  }
  return (
    <div
      className={cn('project-parameters-grid-item', {
        'project-parameters-grid-item__one-column': !predictivePAIEnabled && !predictiveIktosEnabled,
      })}>
      {predictivePAIEnabled && (
        <ul className="params-bunch" aria-label="Predicted parameters">
          {PredictedPaiParams?.allTypes
            .filter((paramName) => !isNil(predictedParametersUpdated[paramName]))
            .map((paramName) => (
              <li key={`predicted-param-${paramName}`}>
                <ParamItem
                  paramName={paramName}
                  value={predictedParametersUpdated[paramName]}
                  paramType="predicted"
                  params={predictedParametersUpdated}
                  isPredicted
                  step={step}
                />
              </li>
            ))}
        </ul>
      )}
      {predictiveIktosEnabled && (
        <ul className="params-bunch" aria-label="Predicted parameters">
          {Object.keys(predictedParametersUpdated).length > 0 &&
            PredictedIktosParams?.allTypes
              .filter(
                (paramName) =>
                  paramName === PredictedIktosParams.INCLUDE_SUBSTRUCTURES ||
                  (paramName === PredictedIktosParams.EXCLUDE_SUBSTRUCTURES && !isNil(predictedParametersUpdated[paramName])) ||
                  (!isNil(predictedParametersUpdated[paramName]) &&
                    paramName !== PredictedIktosParams.INCLUDE_SUBSTRUCTURES &&
                    paramName !== PredictedIktosParams.EXCLUDE_SUBSTRUCTURES)
              )
              .map((paramName) =>
                paramName === PredictedIktosParams.INCLUDE_SUBSTRUCTURES || paramName === PredictedIktosParams.EXCLUDE_SUBSTRUCTURES ? (
                  <li key={`predicted-param-${paramName}`}>
                    <IntermediatesParameter smiles={predictedParametersUpdated[paramName]} paramName={paramName} />
                  </li>
                ) : (
                  <li key={`predicted-param-${paramName}`}>
                    <ParamItem
                      paramName={paramName}
                      value={predictedParametersUpdated[paramName]}
                      paramType="predicted"
                      params={predictedParametersUpdated}
                      isPredicted
                      step={step}
                    />
                  </li>
                )
              )}
        </ul>
      )}
      <ul className="params-bunch" aria-label="Published parameters">
        {PublishedParams?.allTypes
          .filter((paramName) => !isNil(publishedParameters[paramName]))
          .map((paramName) => (
            <li key={`published-param-${paramName}`}>
              <ParamItem
                paramName={paramName}
                value={publishedParameters[paramName]}
                paramType="published"
                params={publishedParameters}
                isPredicted={false}
              />
            </li>
          ))}
      </ul>
    </div>
  );
}

export default ProjectParametersGridItem;
